// src/pages/markets/board/board/ColumnInfoPanel.tsx
//
// One panel for both column kinds. A column with a group shows the group text
// and its live stats; a column without one shows the column text alone, which
// is why the info block is normalised to a single shape before it is rendered.
//
// The panel is wider than the column on purpose: it is a Popper in a portal, so
// it overflows the column rather than being clipped by it.
//
// A bucket key is only clickable when the schema declares it as a filter value:
// the backend answers 400 for a value outside its vocabulary, so a key it does
// not know renders inert instead of sending a request that fails.

import { useMemo, useState } from 'react';
import { Box, IconButton, Link, Tooltip, Typography } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useTheme } from '@mui/material/styles';
import type {
    BoardFilters,
    BoardSchema,
    GroupStatsBucket,
    GroupStatsSignal,
    GroupStatsTotals,
} from '../api/boardTypes';
import { columnDefaults, columnInfo } from '../api/useSchema';
import { useBoardGroups, type BoardGroupsApi } from '../state/boardGroupsContext';
import { useGroupStats } from '../api/useGroupStats';
import { formatAge, formatUsd } from '../card/cardFormat';
import { sideLabels } from '../utils/boardOutcomeLabels';
import { sideColor } from '../utils/sideColor';
import { OUTCOME_MAXLEN_BADGE } from '../../utils/outcomeLabels';
import { COLUMN_BY_ID } from './columnConfig';
import { GroupTextEditor } from './GroupTextEditor';
import { numSx } from '../../../../components/common/page/numberStyles';
import { SectionLabel as BaseSectionLabel } from '../../../../components/common/page/SectionLabel';
import { PANEL_PAD_X, PANEL_ROW_HEIGHT } from '../card/panelParts';

export const COLUMN_INFO_PANEL_WIDTH = 600;
export const COLUMN_INFO_PANEL_MIN_HEIGHT = 320;

// A row or button the phone sheet's reader has to hit.
const TOUCH_ROW = 44;

const DEFAULT_STATS_WINDOW = '24h';

export interface ColumnInfoPanelProps {
    schema: BoardSchema;
    column: string;
    // The resolved category's title, so a heading disambiguated for a label
    // collision reads the same here as in the picker and the column header.
    title?: string;
    filters: BoardFilters;
    now: number;
    statsWindow?: string;
    // The panel is 600 px wide for a Popper; a phone sheet passes '100%'.
    width?: number | string;
    // The phone sheet: rows and buttons of 44 px, text of at least 12 px, and
    // "tap" where the popover says "click".
    touch?: boolean;
    onFiltersChange: (patch: Partial<BoardFilters>) => void;
    onFocusEvent?: (eventId: string) => void;
}

interface PanelInfo {
    label: string;
    oneLiner: string;
    description: string;
    bullets: string[];
    caveat: string | null;
    docUrl: string | null;
}

function panelInfo(
    schema: BoardSchema,
    column: string,
    group: string | null,
    groups: BoardGroupsApi,
): PanelInfo {
    if (group) {
        const g = groups.resolve(group);
        return {
            label: groups.titleOf(group),
            oneLiner: g.oneLiner,
            description: g.description,
            // A group's methodology bullets, caveat and doc link were shipped by
            // the indexer for two of eighteen groups. They are not part of the
            // desk-curated contract; a column without a group still has them.
            bullets: [],
            caveat: null,
            docUrl: null,
        };
    }
    const c = columnInfo(schema, column);
    return {
        label: c?.label ?? COLUMN_BY_ID[column]?.title ?? column,
        oneLiner: c?.one_liner ?? '',
        description: c?.description ?? '',
        bullets: c?.reads ?? [],
        caveat: null,
        docUrl: null,
    };
}

function totalsLine(totals: GroupStatsTotals, window: string): string {
    const sided = totals.yes_count + totals.no_count;
    const parts = [
        `${totals.signal_count} active`,
        `${totals.event_count} events`,
        `${totals.changed_in_window} new ${window}`,
        formatUsd(totals.capital_usd),
    ];
    if (sided > 0) {
        parts.push(`${Math.round((totals.yes_count / sided) * 100)}% YES`);
    }
    return parts.join(' · ');
}

function signalTitle(s: GroupStatsSignal): string {
    return s.title ?? s.question ?? s.event_id;
}

function SectionLabel({ children, note, touch }: { children: React.ReactNode; note?: string; touch?: boolean }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
            <BaseSectionLabel sx={{ mb: 0, ...(touch && { fontSize: '12px' }) }}>{children}</BaseSectionLabel>
            {note && (
                <Typography sx={{ fontSize: touch ? '12px' : '11px', color: 'text.disabled', ml: 'auto' }}>
                    {note}
                </Typography>
            )}
        </Box>
    );
}

function BucketBar({
    bucket,
    max,
    color,
    onSelect,
    touch,
}: {
    bucket: GroupStatsBucket;
    max: number;
    color: string;
    onSelect: ((key: string) => void) | null;
    touch?: boolean;
}) {
    const width = Math.max(2, Math.round((bucket.capital_usd / max) * 100));
    return (
        <Box
            onClick={onSelect ? () => onSelect(bucket.key) : undefined}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                ...(touch ? { minHeight: TOUCH_ROW } : { height: PANEL_ROW_HEIGHT }),
                px: 0.5,
                borderRadius: '3px',
                cursor: onSelect ? 'pointer' : 'default',
                '&:hover': onSelect ? { bgcolor: 'action.hover' } : undefined,
            }}
        >
            <Typography
                sx={{
                    width: 120,
                    flexShrink: 0,
                    fontSize: touch ? '13px' : '12px',
                    color: 'text.secondary',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}
            >
                {bucket.key}
            </Typography>
            <Box
                sx={{
                    flex: 1,
                    minWidth: 0,
                    height: 6,
                    bgcolor: 'action.hover',
                    borderRadius: '3px',
                }}
            >
                <Box
                    sx={{ width: `${width}%`, height: '100%', bgcolor: color, borderRadius: '3px' }}
                />
            </Box>
            <Typography
                sx={{
                    width: 34,
                    flexShrink: 0,
                    fontSize: touch ? '13px' : '12px',
                    ...numSx,
                    color: 'text.secondary',
                    textAlign: 'right',
                }}
            >
                {bucket.signal_count}
            </Typography>
            <Typography
                sx={{
                    width: 64,
                    flexShrink: 0,
                    fontSize: touch ? '13px' : '12px',
                    ...numSx,
                    color: 'text.primary',
                    textAlign: 'right',
                }}
            >
                {formatUsd(bucket.capital_usd)}
            </Typography>
        </Box>
    );
}

function SignalRow({
    signal,
    now,
    onFocusEvent,
    touch,
}: {
    signal: GroupStatsSignal;
    now: number;
    onFocusEvent?: (eventId: string) => void;
    touch?: boolean;
}) {
    const theme = useTheme();
    const labels = useMemo(
        () => sideLabels(signal, OUTCOME_MAXLEN_BADGE),
        [signal],
    );
    const side = signal.capital_usd_yes >= signal.capital_usd_no ? 'YES' : 'NO';
    const sideLabel = side === 'NO' ? labels.no : labels.yes;
    const traders = signal.n_traders_yes + signal.n_traders_no;
    return (
        <Box
            onClick={onFocusEvent ? () => onFocusEvent(signal.event_id) : undefined}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                // The numbers on one line and the title under them: beside
                // them the title is left with a few letters.
                ...(touch ? { flexWrap: 'wrap', minHeight: TOUCH_ROW, py: 0.75 } : { height: PANEL_ROW_HEIGHT }),
                px: 0.5,
                borderRadius: '3px',
                cursor: onFocusEvent ? 'pointer' : 'default',
                '&:hover': onFocusEvent ? { bgcolor: 'action.hover' } : undefined,
            }}
        >
            <Typography
                sx={{
                    width: 64,
                    flexShrink: 0,
                    fontSize: touch ? '13px' : '12px',
                    fontWeight: 700,
                    ...numSx,
                    textAlign: 'right',
                }}
            >
                {formatUsd(signal.dominant_capital)}
            </Typography>
            <Typography
                title={sideLabel.title}
                sx={{
                    width: 104,
                    flexShrink: 0,
                    fontSize: touch ? '13px' : '12px',
                    fontWeight: 700,
                    color: sideColor(labels, side, theme),
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}
            >
                {sideLabel.text}
            </Typography>
            <Typography
                sx={{
                    flex: touch ? '1 1 100%' : 1,
                    minWidth: 0,
                    fontSize: touch ? '13px' : '12px',
                    color: 'text.primary',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    ...(touch && { order: 1 }),
                }}
            >
                {signalTitle(signal)}
            </Typography>
            <Typography
                sx={{
                    flexShrink: 0,
                    fontSize: touch ? '13px' : '12px',
                    color: 'text.disabled',
                    ...numSx,
                    ...(touch && { ml: 'auto' }),
                }}
            >
                {traders} tr · {formatAge(signal.ts, now)}
            </Typography>
        </Box>
    );
}

export function ColumnInfoPanel({
    schema,
    column,
    title,
    filters,
    now,
    statsWindow = DEFAULT_STATS_WINDOW,
    width = COLUMN_INFO_PANEL_WIDTH,
    touch,
    onFiltersChange,
    onFocusEvent,
}: ColumnInfoPanelProps) {
    const theme = useTheme();

    // `!== undefined`, not `??`: a discovery pane's group is legitimately null, and
    // coalescing it to the column default is the shape that broke resolution twice.
    const group =
        filters.group !== undefined ? filters.group : columnDefaults(schema, column).group;
    const groups = useBoardGroups();
    const info = useMemo(
        () => panelInfo(schema, column, group, groups),
        [schema, column, group, groups],
    );
    const accent = group ? groups.resolve(group).color : theme.palette.text.secondary;

    const [editing, setEditing] = useState(false);
    // Offered only for a group (a discovery column's text is the backend's),
    // to an admin, and only once the read succeeded — editing from a failed
    // read would save the humanized fallback over whatever was curated.
    const canEdit = Boolean(group) && groups.isAdmin && groups.isSuccess;

    const stats = useGroupStats({
        group,
        filters,
        window: statsWindow,
        enabled: Boolean(group),
    });

    const categoryVocab = useMemo(
        () => new Set(schema.event_categories),
        [schema.event_categories],
    );
    const tagVocab = useMemo(
        () => new Set(schema.polymarket_tags),
        [schema.polymarket_tags],
    );

    const totals = stats.data?.totals ?? null;
    const categories = stats.data?.by_category ?? [];
    const tags = useMemo(
        () => (stats.data?.by_tag ?? []).filter(t => tagVocab.has(t.key)),
        [stats.data, tagVocab],
    );
    const signals = stats.data?.top_signals ?? [];

    const maxCategory = Math.max(1, ...categories.map(c => c.capital_usd));
    const maxTag = Math.max(1, ...tags.map(t => t.capital_usd));

    const selectCategory = (key: string) =>
        onFiltersChange({ eventsInclude: [key], eventsExclude: [] });
    const selectTag = (key: string) =>
        onFiltersChange({ tagsInclude: [key], tagsExclude: [], tagMatch: 'any' });

    return (
        <Box sx={{ width }}>
            <Box sx={{ px: PANEL_PAD_X, py: 1.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box
                        sx={{
                            width: 7,
                            height: 7,
                            borderRadius: '50%',
                            bgcolor: accent,
                            flexShrink: 0,
                        }}
                    />
                    <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>
                        {title ?? info.label}
                    </Typography>
                    {canEdit && !editing && (
                        <Tooltip title="Rename this group and edit its description">
                            <IconButton
                                size="small"
                                aria-label={`Edit ${group}`}
                                onClick={() => setEditing(true)}
                                sx={touch ? { width: TOUCH_ROW, height: TOUCH_ROW, p: 0 } : { p: 0.25 }}
                            >
                                <EditOutlinedIcon sx={{ fontSize: touch ? 20 : 13 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                    {totals && (
                        <Typography
                            sx={{ fontSize: touch ? '12px' : '11px', color: 'text.disabled', ml: 'auto' }}
                        >
                            {totals.latest_ts
                                ? `live · updated ${formatAge(totals.latest_ts, now)}`
                                : 'live'}
                        </Typography>
                    )}
                </Box>

                {editing && group ? (
                    <GroupTextEditor
                        touch={touch}
                        group={group}
                        initial={{
                            // The stored label, not `title`: a collision suffix
                            // is a display aid, and editing must not save it
                            // into the name.
                            label: groups.resolve(group).label,
                            oneLiner: info.oneLiner,
                            description: info.description,
                        }}
                        onSave={async text => {
                            await groups.saveText(group, text);
                            setEditing(false);
                        }}
                        onCancel={() => setEditing(false)}
                    />
                ) : (
                    <>
                        {info.oneLiner && (
                            <Typography
                                sx={{ fontSize: '12px', color: 'text.primary', mt: 0.75 }}
                            >
                                {info.oneLiner}
                            </Typography>
                        )}
                        {info.description && (
                            <Typography
                                sx={{
                                    fontSize: '12px',
                                    color: 'text.secondary',
                                    mt: 0.5,
                                    whiteSpace: 'pre-line',
                                }}
                            >
                                {info.description}
                            </Typography>
                        )}
                        {/* An admin needs a way in even when there is nothing
                            written yet, which is 16 of the 18 groups today. */}
                        {canEdit && !info.oneLiner && !info.description && (
                            <Typography
                                sx={{
                                    fontSize: '12px',
                                    color: 'text.disabled',
                                    mt: 0.75,
                                    fontStyle: 'italic',
                                }}
                            >
                                No description yet.
                            </Typography>
                        )}
                    </>
                )}

                {info.bullets.length > 0 && (
                    <Box component="ul" sx={{ m: 0, mt: 0.75, pl: 2 }}>
                        {info.bullets.map(b => (
                            <Typography
                                key={b}
                                component="li"
                                sx={{ fontSize: '12px', color: 'text.secondary', mb: 0.25 }}
                            >
                                {b}
                            </Typography>
                        ))}
                    </Box>
                )}

                {info.docUrl && (
                    <Link
                        href={info.docUrl}
                        target="_blank"
                        rel="noreferrer"
                        underline="hover"
                        sx={
                            touch
                                ? { fontSize: '13px', display: 'inline-flex', alignItems: 'center', minHeight: TOUCH_ROW, mt: 0.75 }
                                : { fontSize: '12px', display: 'inline-block', mt: 0.75 }
                        }
                    >
                        Full guide →
                    </Link>
                )}
            </Box>

            {group && (
                <Box sx={{ borderTop: 1, borderColor: 'divider', px: PANEL_PAD_X, py: 1.25 }}>
                    {stats.error && (
                        <Typography sx={{ fontSize: '12px', color: 'error.main' }}>
                            The group stats did not load.
                        </Typography>
                    )}

                    {!stats.error && !totals && (
                        <Typography sx={{ fontSize: '12px', color: 'text.disabled' }}>
                            Loading group stats…
                        </Typography>
                    )}

                    {totals && (
                        <Typography
                            sx={{
                                fontSize: '12px',
                                fontWeight: 700,
                                ...numSx,
                                mb: 1,
                            }}
                        >
                            {totalsLine(totals, statsWindow)}
                        </Typography>
                    )}

                    {categories.length > 0 && (
                        <Box sx={{ mb: 1 }}>
                            <SectionLabel touch={touch} note={touch ? 'tap to filter' : 'click to filter'}>Categories</SectionLabel>
                            {categories.map(c => (
                                <BucketBar
                                    key={c.key}
                                    bucket={c}
                                    max={maxCategory}
                                    color={accent}
                                    onSelect={categoryVocab.has(c.key) ? selectCategory : null}
                                    touch={touch}
                                />
                            ))}
                        </Box>
                    )}

                    {tags.length > 0 && (
                        <Box sx={{ mb: 1 }}>
                            <SectionLabel touch={touch} note={touch ? 'tap to filter' : 'click to filter'}>Tags</SectionLabel>
                            {tags.map(t => (
                                <BucketBar
                                    key={t.key}
                                    bucket={t}
                                    max={maxTag}
                                    color={accent}
                                    onSelect={selectTag}
                                    touch={touch}
                                />
                            ))}
                        </Box>
                    )}

                    {signals.length > 0 && (
                        <Box>
                            <SectionLabel touch={touch} note={`sorted by capital · ${statsWindow} flag`}>
                                Biggest signals
                            </SectionLabel>
                            {signals.map(s => (
                                <SignalRow
                                    key={`${s.event_id}:${s.market_id}`}
                                    signal={s}
                                    now={now}
                                    onFocusEvent={onFocusEvent}
                                    touch={touch}
                                />
                            ))}
                        </Box>
                    )}

                    {info.caveat && (
                        <Typography sx={{ fontSize: '12px', color: 'text.disabled', mt: 1 }}>
                            {info.caveat}
                        </Typography>
                    )}
                </Box>
            )}
        </Box>
    );
}